import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import DocumentAlertsWidget from '@/components/DocumentAlertsWidget';
import UpcomingEventsWidget from '@/components/UpcomingEventsWidget';
import UpcomingTasksWidget from '@/components/UpcomingTasksWidget';
import CeeStatsWidget from '@/components/CeeStatsWidget';
import CopilotChat from '@/components/CopilotChat';
import {
  Building2, Wrench, FileText, HardHat, CheckCircle2, Send, ShieldCheck,
  AlertTriangle, Loader2, TrendingUp, Target, FileSignature, ClipboardList,
  ChevronRight, Bell, Clock, Euro, Users, HardHat as HardHatIcon, PackageCheck, Calendar as CalendarIcon, Receipt,
} from 'lucide-react';

const fmtMoney = (n) => n == null ? '-' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return iso; }
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [chantiersOverview, setChantiersOverview] = useState({ upcoming_chantiers: [], alertes_materiel: [], facturation_en_attente: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Dashboard - Reno Resolve';
    (async () => {
      try {
        const [r, rCh] = await Promise.all([
          api.get('/dashboard/overview'),
          api.get('/dashboard/chantiers-overview').catch(() => ({ data: { upcoming_chantiers: [], alertes_materiel: [], facturation_en_attente: [] } })),
        ]);
        setData(r.data);
        setChantiersOverview(rCh.data);
      } catch (e) {
        console.error(e);
        toast.error('Erreur chargement dashboard');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }
  if (!data) return null;

  const { collectivites, pipeline, finance, rentabilite_distribution, actions_pending, devis_a_relancer, recent_fiches, recent_devis, recent_chantiers } = data;
  const rentTotal = (rentabilite_distribution.green || 0) + (rentabilite_distribution.orange || 0) + (rentabilite_distribution.red || 0);

  // Mark actions completed (optimistic)
  const handleCompleteAction = async (clientId, actionId) => {
    if (!actionId) { toast.error('Action sans identifiant'); return; }
    try {
      await api.put(`/gestion-clients/action/${actionId}/complete`);
      // Reload dashboard
      const r = await api.get('/dashboard/overview');
      setData(r.data);
      toast.success('Action marquée comme faite');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du marquage');
    }
  };

  const highCount = actions_pending.filter((a) => a.priority === 'high').length;
  const mediumCount = actions_pending.filter((a) => a.priority === 'medium').length;
  const totalActions = actions_pending.length;

  return (
    <>
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tableau de bord</h1>
          <p className="text-sm text-slate-600 mt-1">Vue d'ensemble Reno Resolve</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/gestion-clients')}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="dash-open-collectivites-btn">
            <Building2 className="w-4 h-4" /> Espace Clients
          </button>
        </div>
      </div>

      {/* HERO: Actions en attente — détectées via boîte mail (tous types de contacts) */}
      <DocumentAlertsWidget />

      {/* Prochains événements Agenda + Tâches côte à côte */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UpcomingEventsWidget />
        <UpcomingTasksWidget />
      </div>

      {/* NEW — Pilotage Chantiers : timeline + alertes matériel + facturation en attente */}
      <ChantiersPilotageSection data={chantiersOverview} navigate={navigate} />

      {/* Widget Performance CEE */}
      <CeeStatsWidget />

      {/* Top row: 4 primary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <PrimaryKPI label="Collectivités" value={collectivites.total} icon={Building2} color="blue"
                    onClick={() => navigate('/gestion-clients')} testid="kpi-collectivites" />
        <PrimaryKPI label="Dossiers actifs"
                    value={(pipeline.devis_brouillon || 0) + (pipeline.devis_envoyes || 0) + (pipeline.devis_signes || 0)}
                    icon={Wrench} color="orange"
                    onClick={() => navigate('/devis')} testid="kpi-actifs" />
        <PrimaryKPI label="Chantiers en cours" value={pipeline.chantiers_actifs} icon={HardHat} color="amber"
                    onClick={() => navigate('/chantiers')} testid="kpi-chantiers" />
        <PrimaryKPI label="CA signé YTD" value={fmtMoney(finance.ca_ytd_ht)} icon={TrendingUp} color="emerald"
                    hint={`${finance.progress_pct}% / ${fmtMoney(finance.ca_objectif_annuel)}`} testid="kpi-ca" />
      </div>

      {/* CA progress bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-emerald-600" />
            <h3 className="font-semibold text-slate-900 text-sm">Objectif annuel</h3>
          </div>
          <span className="text-xs text-slate-500">
            {fmtMoney(finance.ca_ytd_ht)} sur {fmtMoney(finance.ca_objectif_annuel)} ({finance.progress_pct}%)
          </span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all"
               style={{ width: `${Math.min(100, finance.progress_pct)}%` }} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-slate-100">
          <MiniStat label="Marge cible" value={`${finance.marge_cible}%`} />
          <MiniStat label="Seuil orange" value={`${finance.marge_seuil_orange}%`} />
          <MiniStat label="Marge moyenne observée"
                    value={finance.marge_moyenne_pct != null ? `${finance.marge_moyenne_pct}%` : '—'}
                    highlight={finance.marge_moyenne_pct >= finance.marge_cible ? 'green' :
                               finance.marge_moyenne_pct >= finance.marge_seuil_orange ? 'orange' : 'red'} />
          <MiniStat label="Charges fixes" value={`${finance.coef_charges_pct}% du HT`} />
        </div>
      </div>

      {/* Pipeline flow */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-blue-600" />
            <h3 className="font-semibold text-slate-900 text-sm">Pipeline travaux</h3>
          </div>
          <button onClick={() => navigate('/gestion-clients')} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">
            Voir tout <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <PipelineStep icon={ClipboardList} label="Visites"    count={pipeline.fiches_visite}   color="slate" />
          <PipelineArrow />
          <PipelineStep icon={FileText}      label="Brouillon"  count={pipeline.devis_brouillon} color="blue" />
          <PipelineArrow />
          <PipelineStep icon={Send}          label="Envoyés"    count={pipeline.devis_envoyes}   color="indigo" />
          <PipelineArrow />
          <PipelineStep icon={CheckCircle2}  label="Signés"     count={pipeline.devis_signes}    color="emerald" />
          <PipelineArrow />
          <PipelineStep icon={FileSignature} label="BC reçus"   count={pipeline.bc_recus}        color="amber" />
          <PipelineArrow />
          <PipelineStep icon={HardHat}       label="Chantiers"  count={pipeline.chantiers_actifs} color="orange" />
        </div>
      </div>

      {/* Row: Rentabilité + Actions en attente */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Rentabilité distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <h3 className="font-semibold text-slate-900 text-sm">Rentabilité des devis</h3>
          </div>
          {rentTotal === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Aucun devis avec coûts saisis pour le moment.</p>
          ) : (
            <>
              <div className="space-y-3">
                <RentabiliteRow label="À signer" count={rentabilite_distribution.green || 0}
                                total={rentTotal} color="emerald" />
                <RentabiliteRow label="Arbitrage" count={rentabilite_distribution.orange || 0}
                                total={rentTotal} color="amber" />
                <RentabiliteRow label="À ne pas signer" count={rentabilite_distribution.red || 0}
                                total={rentTotal} color="red" />
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                {rentabilite_distribution.neutral > 0 && (
                  <span>
                    +{rentabilite_distribution.neutral} devis sans coûts saisis
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Devis à relancer */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <h3 className="font-semibold text-slate-900 text-sm">Devis à relancer</h3>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                {devis_a_relancer.length}
              </span>
            </div>
          </div>
          {devis_a_relancer.length === 0 ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Aucun devis à relancer.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {devis_a_relancer.slice(0, 5).map((d) => (
                <button key={d.devis_id} onClick={() => navigate(`/devis/${d.devis_id}`)}
                  className="w-full flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 text-left">
                  <Clock className="mt-0.5 w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 truncate">Relancer devis {d.devis_id}</p>
                    <p className="text-xs text-slate-500">
                      {(d.client_info || {}).nom || '—'} · {fmtMoney(d.montant_estime)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row: recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <RecentCard title="Visites récentes" icon={ClipboardList} color="blue"
                    items={recent_fiches} emptyMsg="Aucune fiche visite"
                    renderItem={(f) => ({
                      title: f.client_nom || 'Sans client',
                      subtitle: f.types_travaux?.join(', ') || '—',
                      right: fmtDate(f.created_at),
                      href: '/fiches-visite',
                    })} />
        <RecentCard title="Derniers devis" icon={FileText} color="indigo"
                    items={recent_devis} emptyMsg="Aucun devis"
                    renderItem={(d) => ({
                      title: d.project_title || (d.client_info || {}).nom || d.devis_id,
                      subtitle: `${d.statut} · ${fmtMoney(d.montant_estime || d.montant_total)}`,
                      right: fmtDate(d.date_creation),
                      href: `/devis/${d.devis_id}`,
                    })} />
        <RecentCard title="Chantiers récents" icon={HardHat} color="orange"
                    items={recent_chantiers} emptyMsg="Aucun chantier"
                    renderItem={(c) => ({
                      title: c.ville || c.adresse_chantier || c.chantier_id,
                      subtitle: `${c.statut} · ${fmtMoney(c.budget_prevu)}`,
                      right: fmtDate(c.date_creation),
                      href: '/chantiers',
                    })} />
      </div>
    </div>
    <CopilotChat />
    </>
  );
}

function PrimaryKPI({ label, value, icon: Icon, color, hint, onClick, testid }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700',
    orange: 'bg-orange-50 text-orange-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
  };
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag onClick={onClick} data-testid={testid}
      className={`w-full text-left bg-white rounded-xl border border-slate-200 p-4 ${onClick ? 'hover:shadow-md hover:border-blue-300 transition-all cursor-pointer' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${colors[color] || colors.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 leading-tight">{value}</p>
          {hint && <p className="text-xs text-slate-400 truncate">{hint}</p>}
        </div>
      </div>
    </Tag>
  );
}

function MiniStat({ label, value, highlight }) {
  const colors = {
    green: 'text-emerald-700', orange: 'text-amber-700', red: 'text-red-700',
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm font-bold ${colors[highlight] || 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

function PipelineStep({ icon: Icon, label, count, color }) {
  const colors = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-blue-100 text-blue-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    orange: 'bg-orange-100 text-orange-700',
  };
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colors[color] || colors.slate}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-slate-900 leading-none">{count}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}

function PipelineArrow() {
  return (
    <div className="hidden md:flex items-center justify-center">
      <ChevronRight className="w-5 h-5 text-slate-300" />
    </div>
  );
}

function RentabiliteRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const colors = {
    emerald: { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-100' },
    amber:   { bar: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-100' },
    red:     { bar: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-100' },
  };
  const c = colors[color];
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className={`font-medium ${c.text}`}>{label}</span>
        <span className="text-slate-500">{count} · {pct}%</span>
      </div>
      <div className={`h-2 ${c.bg} rounded-full overflow-hidden`}>
        <div className={`h-full ${c.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Action card for the hero section — shows a single AI-detected action with priority,
// client + category, and a quick "mark as done" button.
function ActionCard({ action, onComplete, navigate }) {
  const [busy, setBusy] = useState(false);
  const isHigh = action.priority === 'high';
  const isMed = action.priority === 'medium';
  const ring = isHigh ? 'border-red-200' : isMed ? 'border-amber-200' : 'border-slate-200';
  const dot  = isHigh ? 'bg-red-500'    : isMed ? 'bg-amber-500'    : 'bg-slate-300';
  const catLabel = {
    collectivites: 'Collectivité',
    particuliers: 'Particulier',
    syndic: 'Syndic',
    syndics: 'Syndic',
    btp: 'BTP',
    autre: 'Autre',
  }[(action.client_category || '').toLowerCase()] || (action.client_category || 'Client');

  const handle = async () => {
    setBusy(true);
    await onComplete(action.client_id, action.action_id);
    setBusy(false);
  };

  return (
    <div className={`group bg-white border ${ring} rounded-xl p-3 hover:shadow-sm transition-shadow flex items-start gap-3`}>
      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${dot}`} />
      <button
        onClick={() => navigate(`/gestion-clients`)}
        className="flex-1 min-w-0 text-left"
        title="Ouvrir la fiche client"
      >
        <p className="text-sm font-medium text-slate-900 leading-snug">{action.action}</p>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <span className="text-[11px] font-medium text-slate-700 truncate">{action.client_nom}</span>
          <span className="text-[10px] px-1.5 py-0 bg-slate-100 text-slate-600 rounded">{catLabel}</span>
          {action.auto_detected && (
            <span className="text-[10px] px-1.5 py-0 bg-violet-50 text-violet-700 rounded">IA</span>
          )}
          {action.date && (
            <span className="text-[10px] text-slate-400">· {fmtDate(action.date)}</span>
          )}
        </div>
        {action.contexte && (
          <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{action.contexte}</p>
        )}
      </button>
      <button
        onClick={handle}
        disabled={busy}
        className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
        title="Marquer comme faite"
        data-testid={`complete-action-${action.action_id}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function RecentCard({ title, icon: Icon, color, items, emptyMsg, renderItem }) {
  const colors = {
    blue: 'text-blue-600',
    indigo: 'text-indigo-600',
    orange: 'text-orange-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${colors[color] || 'text-slate-600'}`} />
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
      </div>
      {(!items || items.length === 0) ? (
        <p className="text-sm text-slate-400 text-center py-4">{emptyMsg}</p>
      ) : (
        <div className="space-y-1">
          {items.slice(0, 5).map((it, i) => {
            const r = renderItem(it);
            return (
              <a key={i} href={r.href}
                className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{r.title}</p>
                  <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>
                </div>
                <span className="text-xs text-slate-400 whitespace-nowrap">{r.right}</span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ------------------ Chantiers Pilotage — Section Dashboard ------------------
function ChantiersPilotageSection({ data, navigate }) {
  const { upcoming_chantiers = [], alertes_materiel = [], facturation_en_attente = [], chantiers_en_cours = [] } = data || {};
  const hasContent = upcoming_chantiers.length + alertes_materiel.length + facturation_en_attente.length + chantiers_en_cours.length > 0;

  return (
    <div
      className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/40 via-white to-indigo-50/30 p-3 md:p-6 shadow-sm"
      data-testid="dashboard-chantiers-pilotage"
    >
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-blue-100 ring-1 ring-blue-200 flex items-center justify-center shrink-0">
            <HardHatIcon className="w-4 h-4 md:w-5 md:h-5 text-blue-700" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base md:text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pilotage chantiers
            </h2>
            <p className="text-[11px] md:text-sm text-slate-600 truncate">
              Timeline, alertes matériel et facturation en attente
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/chantiers')}
          className="text-xs md:text-sm font-medium text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 shrink-0"
          data-testid="dash-open-chantiers-btn"
        >
          <span className="hidden sm:inline">Voir tous</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {!hasContent ? (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <PackageCheck className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-700">Tout est sous contrôle !</p>
          <p className="text-xs text-slate-500">Aucun chantier à planifier, aucune alerte matériel, aucune facture en attente.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* CHANTIERS EN COURS — barre riche */}
          {chantiers_en_cours.length > 0 && (
            <div className="bg-white rounded-xl border border-green-200 p-4" data-testid="pilotage-en-cours">
              <div className="flex items-center gap-2 mb-3">
                <HardHatIcon className="w-4 h-4 text-green-600" />
                <h3 className="text-sm font-semibold text-slate-900">Chantiers en cours</h3>
                <span className="ml-auto text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                  {chantiers_en_cours.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {chantiers_en_cours.slice(0, 6).map((c) => {
                  const marge = c.marge_estimee || 0;
                  const margeColor = marge >= 0 ? 'text-emerald-700' : 'text-red-700';
                  return (
                    <button
                      key={c.chantier_id}
                      type="button"
                      onClick={() => navigate(`/chantiers/${c.chantier_id}`)}
                      className="text-left p-3 rounded-lg border border-slate-200 hover:border-green-400 hover:bg-green-50/40 transition-colors"
                      data-testid={`pilotage-encours-${c.chantier_id}`}
                    >
                      <p className="text-sm font-semibold text-slate-900 truncate">{c.nom}</p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {c.ville || '—'}
                        {c.date_fin_estimee && <span> · fin {fmtDate(c.date_fin_estimee)}</span>}
                      </p>
                      {c.materiel_count > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${c.materiel_progression_pct === 100 ? 'bg-emerald-500' : 'bg-amber-400'}`}
                              style={{ width: `${c.materiel_progression_pct}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-slate-500">Mat. {c.materiel_progression_pct}%</span>
                        </div>
                      )}
                      <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px]">
                        <div className="text-center">
                          <p className="text-slate-400">Budget</p>
                          <p className="font-semibold text-slate-900">{fmtMoney(c.budget)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-slate-400">Dépensé</p>
                          <p className="font-semibold text-rose-700">{fmtMoney(c.depenses_reelles)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-slate-400">Marge</p>
                          <p className={`font-semibold ${margeColor}`}>{fmtMoney(marge)}</p>
                        </div>
                      </div>
                      <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-2">
                        <span>👷 {c.artisans_count} artisan(s)</span>
                        {c.materiel_count > 0 && <span>· 📦 {c.materiel_count} matériel(s)</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Timeline mini-planning */}
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="pilotage-upcoming">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-slate-900">5 prochains chantiers</h3>
            </div>
            {upcoming_chantiers.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-3">Aucun chantier planifié à venir.</p>
            ) : (
              <div className="space-y-2">
                {upcoming_chantiers.map((c) => {
                  const materielTotal = (c.materiel_prevu || []).length;
                  const materielOK = (c.materiel_prevu || []).filter(m => ['Commandé', 'Livré', 'Posé'].includes(m.statut || 'À chiffrer')).length;
                  return (
                    <button
                      key={c.chantier_id}
                      type="button"
                      onClick={() => navigate(`/chantiers/${c.chantier_id}`)}
                      className="w-full text-left p-2.5 rounded-lg border border-slate-100 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                      data-testid={`pilotage-upcoming-${c.chantier_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-slate-900 truncate">
                            {c.nom_interne || c.project_title || c.ville || c.chantier_id}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {fmtDate(c.date_debut)} → {fmtDate(c.date_fin_estimee)}
                          </p>
                        </div>
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${
                          c.statut === 'En cours' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {c.statut}
                        </span>
                      </div>
                      {materielTotal > 0 && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${materielOK === materielTotal ? 'bg-emerald-500' : 'bg-amber-400'}`}
                              style={{ width: `${(materielOK / materielTotal) * 100}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-slate-500">
                            Mat. {materielOK}/{materielTotal}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Alertes matériel */}
          <div className="bg-white rounded-xl border border-red-200 p-4" data-testid="pilotage-alertes">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <h3 className="text-sm font-semibold text-slate-900">Alertes matériel</h3>
              {alertes_materiel.length > 0 && (
                <span className="ml-auto text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold">
                  {alertes_materiel.length}
                </span>
              )}
            </div>
            {alertes_materiel.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-3">Aucune alerte — tout le matériel des chantiers imminents est OK.</p>
            ) : (
              <div className="space-y-2">
                {alertes_materiel.map((a) => (
                  <button
                    key={a.chantier_id}
                    type="button"
                    onClick={() => navigate(`/chantiers/${a.chantier_id}`)}
                    className="w-full text-left p-2.5 rounded-lg border border-red-100 hover:border-red-300 hover:bg-red-50/50 transition-colors"
                    data-testid={`pilotage-alerte-${a.chantier_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-900 truncate">{a.nom}</p>
                      <span className="text-[10px] text-red-700 font-semibold whitespace-nowrap">
                        Début {fmtDate(a.date_debut)}
                      </span>
                    </div>
                    <p className="text-[11px] text-red-600 mt-0.5">
                      {a.nb_materiel_non_commande} matériel(s) non commandé(s)
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Facturation en attente */}
          <div className="bg-white rounded-xl border border-amber-200 p-4" data-testid="pilotage-facturation">
            <div className="flex items-center gap-2 mb-3">
              <Receipt className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-slate-900">Facturation en attente</h3>
              {facturation_en_attente.length > 0 && (
                <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">
                  {facturation_en_attente.length}
                </span>
              )}
            </div>
            {facturation_en_attente.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-3">Aucun chantier terminé sans facture émise.</p>
            ) : (
              <div className="space-y-2">
                {facturation_en_attente.map((f) => (
                  <button
                    key={f.chantier_id}
                    type="button"
                    onClick={() => navigate(`/chantiers/${f.chantier_id}`)}
                    className="w-full text-left p-2.5 rounded-lg border border-amber-100 hover:border-amber-300 hover:bg-amber-50/50 transition-colors"
                    data-testid={`pilotage-facturation-${f.chantier_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-900 truncate">{f.nom}</p>
                      <span className="text-xs font-bold text-amber-800 whitespace-nowrap">
                        {fmtMoney(f.montant)}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Fin {fmtDate(f.date_fin_estimee)} · Devis {f.devis_id}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
